import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getStudents, getMembers, getVerificationLogs } from '../services/api';
import StudentsPage from './StudentsPage';
import MembersPage from './MembersPage';
import { useLanguage } from '../LanguageContext';

function DashboardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();
  const [totalRegistrations, setTotalRegistrations] = useState(0);
  const [totalMembers, setTotalMembers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showAttendanceMenu, setShowAttendanceMenu] = useState(false);
  const [currentView, setCurrentView] = useState('dashboard');
  const [verificationLogs, setVerificationLogs] = useState([]);
  const username = localStorage.getItem('username');
  const userRole = localStorage.getItem('role');

  useEffect(() => {
    if (!localStorage.getItem('token')) {
      navigate('/login');
      return;
    }
    if (localStorage.getItem('role') === 'VERIFIER') {
      navigate('/verify');
      return;
    }
    loadStats();
  }, []);

  const loadStats = async () => {
    setLoading(true);
    try {
      const response = await getStudents();
      setTotalRegistrations(response.data.length);
      const membersRes = await getMembers();
      setTotalMembers(membersRes.data.length);
    } catch (error) {
      console.error('Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  const handleWidgetClick = () => {
    setCurrentView('students');
  };

  return (
    <div style={{display: 'flex', minHeight: '100vh'}}>
      <div style={{width: '250px', backgroundColor: '#f8f9fa', borderRight: '1px solid #dee2e6', padding: '20px'}}>
        <div style={{marginBottom: '30px', textAlign: 'center'}}>
          <img src="/misk_logo.png" alt="MISK Logo" style={{height: '60px'}} />
        </div>
        <nav>
          <div 
            style={{padding: '12px', cursor: 'pointer', borderRadius: '5px', backgroundColor: currentView === 'dashboard' ? '#e8f5e9' : 'transparent', color: '#2d5f3f', fontWeight: '500', marginBottom: '10px'}}
            onClick={() => setCurrentView('dashboard')}
          >
            📊 Dashboard
          </div>
          <div 
            style={{padding: '12px', cursor: 'pointer', borderRadius: '5px', backgroundColor: currentView === 'students' ? '#e8f5e9' : 'transparent', color: '#2d5f3f', fontWeight: '500', marginBottom: '10px'}}
            onClick={() => setCurrentView('students')}
          >
            👥 Students
          </div>
          <div 
            style={{padding: '12px', cursor: 'pointer', borderRadius: '5px', backgroundColor: currentView === 'members' ? '#e8f5e9' : 'transparent', color: '#2d5f3f', fontWeight: '500', marginBottom: '10px'}}
            onClick={() => setCurrentView('members')}
          >
            🪪 Members
          </div>
          {(userRole === 'ADMIN' || userRole === 'MASTER') && (
            <div 
              style={{padding: '12px', cursor: 'pointer', borderRadius: '5px', backgroundColor: currentView === 'users' ? '#e8f5e9' : 'transparent', color: '#2d5f3f', fontWeight: '500', marginBottom: '10px'}}
              onClick={() => navigate('/users')}
            >
              👤 Users
            </div>
          )}
          {(userRole === 'ADMIN' || userRole === 'MASTER') && (
            <div style={{marginBottom: '10px'}}>
              <div 
                style={{padding: '12px', cursor: 'pointer', borderRadius: '5px', backgroundColor: showAttendanceMenu ? '#e8f5e9' : 'transparent', color: '#2d5f3f', fontWeight: '500'}}
                onClick={() => setShowAttendanceMenu(!showAttendanceMenu)}
              >
                📋 Attendance {showAttendanceMenu ? '▼' : '▶'}
              </div>
              {showAttendanceMenu && (
                <>
                  <div 
                    style={{paddingLeft: '30px', paddingTop: '8px', cursor: 'pointer', padding: '8px 8px 8px 30px', borderRadius: '5px', color: '#555'}}
                    onClick={() => navigate('/attendance-sheet')}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#e8f5e9'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    Generate Sheet
                  </div>
                  <div 
                    style={{paddingLeft: '30px', cursor: 'pointer', padding: '8px 8px 8px 30px', borderRadius: '5px', color: '#555'}}
                    onClick={() => navigate('/upload-attendance')}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#e8f5e9'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    Upload Offline Sheet
                  </div>
                  <div 
                    style={{paddingLeft: '30px', cursor: 'pointer', padding: '8px 8px 8px 30px', borderRadius: '5px', color: '#555'}}
                    onClick={() => navigate('/online-attendance')}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#e8f5e9'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    Online Attendance
                  </div>
                </>
              )}
            </div>
          )}
          {(userRole === 'ADMIN' || userRole === 'MASTER') && (
            <div 
              style={{padding: '12px', cursor: 'pointer', borderRadius: '5px', backgroundColor: currentView === 'verification-logs' ? '#e8f5e9' : 'transparent', color: '#2d5f3f', fontWeight: '500', marginBottom: '10px'}}
              onClick={() => { setCurrentView('verification-logs'); getVerificationLogs().then(res => setVerificationLogs(res.data)).catch(() => {}); }}
            >
              🔍 Verification Logs
            </div>
          )}
        </nav>
      </div>

      <div style={{flex: 1}}>
        <div className="container">
          <div className="header">
            <h1 style={{margin: 0}}>MISK {currentView === 'dashboard' ? t('dashboard') : currentView === 'members' ? 'Members' : currentView === 'verification-logs' ? 'Verification Logs' : 'Students'}</h1>
            <div style={{display: 'flex', alignItems: 'center', gap: '15px'}}>
              <select value={language} onChange={(e) => setLanguage(e.target.value)} className="lang-select">
                <option value="en">English</option>
                <option value="ta">தமிழ்</option>
                <option value="ar">العربية</option>
              </select>
              <span>Welcome, {username}</span>
              <button className="btn btn-logout" onClick={handleLogout}>{t('logout')}</button>
            </div>
          </div>

          {currentView === 'dashboard' ? (
            <div className="card">
              <h2>Overview</h2>
              {loading ? (
                <div style={{textAlign: 'center', padding: '60px 0'}}>
                  <div className="spinner"></div>
                </div>
              ) : (
                <>
                <div style={{display: 'flex', gap: '20px', flexWrap: 'wrap'}}>
                <div 
                  onClick={handleWidgetClick}
                  style={{
                    padding: '30px',
                    backgroundColor: '#2d5f3f',
                    color: 'white',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'transform 0.2s',
                    maxWidth: '300px'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  <div style={{fontSize: '48px', fontWeight: 'bold', marginBottom: '10px'}}>
                    {totalRegistrations}
                  </div>
                  <div style={{fontSize: '18px'}}>Total Registrations</div>
                </div>
                <div 
                  onClick={() => setCurrentView('members')}
                  style={{
                    padding: '30px',
                    backgroundColor: '#1565c0',
                    color: 'white',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'transform 0.2s',
                    maxWidth: '300px'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  <div style={{fontSize: '48px', fontWeight: 'bold', marginBottom: '10px'}}>
                    {totalMembers}
                  </div>
                  <div style={{fontSize: '18px'}}>Member Registrations</div>
                </div>
                </div>
                <div style={{marginTop: '20px'}}>
                  <a href="/learningcenter/membership" target="_blank" rel="noopener noreferrer"
                    style={{color: '#1565c0', textDecoration: 'none', fontSize: '14px', fontWeight: '500'}}>
                    🔗 Open Member Registration Form
                  </a>
                </div>
                </>
              )}
            </div>
          ) : currentView === 'members' ? (
            <MembersPage />
          ) : currentView === 'verification-logs' ? (
            <div className="card">
              <h2>Verification Logs</h2>
              {verificationLogs.length === 0 ? (
                <p style={{color:'#666',textAlign:'center',padding:'40px 0'}}>No verification logs yet</p>
              ) : (
                <div style={{overflowX:'auto'}}>
                  <table style={{width:'100%',borderCollapse:'collapse'}}>
                    <thead>
                      <tr style={{background:'#2d5f3f',color:'#fff'}}>
                        <th style={{padding:'12px',textAlign:'left'}}>Civil ID</th>
                        <th style={{padding:'12px',textAlign:'left'}}>Member Name</th>
                        <th style={{padding:'12px',textAlign:'left'}}>Verified By</th>
                        <th style={{padding:'12px',textAlign:'center'}}>Result</th>
                        <th style={{padding:'12px',textAlign:'left'}}>Date/Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {verificationLogs.sort((a,b) => new Date(b.verifiedAt) - new Date(a.verifiedAt)).map((log, i) => (
                        <tr key={log.id || i} style={{borderBottom:'1px solid #eee'}}>
                          <td style={{padding:'12px'}}>{log.civilIdNo}</td>
                          <td style={{padding:'12px'}}>{log.memberName || '-'}</td>
                          <td style={{padding:'12px'}}>{log.verifiedBy}</td>
                          <td style={{padding:'12px',textAlign:'center'}}>
                            <span style={{padding:'4px 10px',borderRadius:'4px',fontSize:'12px',fontWeight:'bold',color:'#fff',
                              background: log.result === 'VERIFIED' ? '#4caf50' : log.result === 'EXPIRED' ? '#f44336' : log.result === 'NOT_APPROVED' ? '#ff9800' : '#9e9e9e'}}>
                              {log.result}
                            </span>
                          </td>
                          <td style={{padding:'12px'}}>{log.verifiedAt ? new Date(log.verifiedAt).toLocaleString() : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            <StudentsPage />
          )}
        </div>
      </div>
    </div>
  );
}

export default DashboardPage;
